/** 起動パラメータの取得関数（main.ts の info.get と同じシグネチャ） */
type StartupParameterGetter = (key: string) => string;

/** シェアモードの判定に使う起動パラメータのキー */
const SHARE_TOKEN_PARAM_KEY = 'share_token';

/** 開発環境でシェアモードを強制するための起動パラメータのキー */
const SHARE_MODE_PARAM_KEY = 'share';

/** 開発環境でシェアモードを強制する場合の起動パラメータの値 */
const SHARE_MODE_PARAM_ON = 'on';

/** シェアモード時に body へ付与するクラス名 */
const SHARE_MODE_BODY_CLASS_NAME = 'shareMode';

/** 開発環境でシェアモードを強制した場合に body へ付与するクラス名 */
const SHARE_MODE_DEBUG_BODY_CLASS_NAME = 'shareModeDebug';

/** Authorization ヘッダのトークン種別 */
const BEARER_PREFIX = 'Bearer ';

/**
 * シェアモードマネージャー
 *
 * アカウントを持たないユーザー（共有URLからの閲覧者）による起動を
 * 「シェアモード」として管理する。
 * 共有リンクのURL（末尾に ?share_token=... が付く）と、サイト側が保存した起動情報の
 * 両方に共有トークンがある場合をシェアモードとみなす。
 * 開発環境（localhost / debug_mode_enabled）では、デバッグモードと同様に
 * 起動パラメータ share=on でもシェアモードとして起動できる。
 *
 * API は共有ルート（/api/v1/share/webcad/...）を使い、
 * 認証は共有トークンを Authorization ヘッダに載せて行う。
 */
export default class ShareModeManager {
  // 共有トークン（シェアモードでない場合は空文字）
  private _shareToken: string = '';

  // 開発環境で起動パラメータによりシェアモードを強制しているか
  private _isForcedShareMode: boolean = false;

  /**
   * 起動パラメータからシェアモードを判定する
   * @param getUrlParameter URLクエリの取得関数
   * @param getStoredParameter サイト側が保存した起動情報（localStorage）の取得関数
   * @param isDevelopmentEnvironment 開発環境（localhost / debug_mode_enabled）かどうか
   */
  public initialize(
    getUrlParameter: StartupParameterGetter,
    getStoredParameter: StartupParameterGetter,
    isDevelopmentEnvironment: boolean,
  ): void {
    // 共有リンク（末尾に ?share_token=... が付く）と、サイト側が保存した起動情報の
    // 両方に共有トークンがある場合をシェアモードとする。
    // 片方だけの場合は前回の共有起動が残っているだけとみなし、通常モードで起動する。
    const urlShareToken: string = getUrlParameter(SHARE_TOKEN_PARAM_KEY);
    const storedShareToken: string = getStoredParameter(SHARE_TOKEN_PARAM_KEY);
    this._shareToken = (urlShareToken !== '' && storedShareToken !== '') ? urlShareToken : '';

    // 開発環境では共有トークンなしでもシェアモードを確認できるようにする
    this._isForcedShareMode = isDevelopmentEnvironment
      && getUrlParameter(SHARE_MODE_PARAM_KEY) === SHARE_MODE_PARAM_ON;
    this.applyBodyClass();
  }

  /** シェアモードで起動しているか */
  public get isShareMode(): boolean {
    return this._shareToken !== '' || this._isForcedShareMode;
  }

  /** 共有トークン（シェアモードでない場合は空文字） */
  public get shareToken(): string {
    return this._shareToken;
  }

  /**
   * 共有トークンによる認証を行うか
   * デバッグ起動（share=on）はトークンを持たないため、通常の認証のままにする
   */
  public get usesShareToken(): boolean {
    return this._shareToken !== '';
  }

  /**
   * Authorization ヘッダに設定する共有トークン
   * 起動パラメータで Bearer が付いている場合は二重に付けない
   */
  public get shareTokenAuthorization(): string {
    if (this._shareToken === '') return '';
    if (this._shareToken.startsWith(BEARER_PREFIX)) return this._shareToken;
    return `${BEARER_PREFIX}${this._shareToken}`;
  }

  /**
   * モデル上のアイテムを操作（移動・回転・サイズ変更・複製・削除）できるか
   * シェアモードでは選択のみ可能とし、操作アイコン（ピッカー）を表示しない
   */
  public get canOperateModel(): boolean {
    return !this.isShareMode;
  }

  /**
   * シェアモードをCSSからも判別できるよう body にクラスを付与する
   * デバッグ起動（share=on）は動作確認用のメニューを出し分けるため別クラスにする
   */
  private applyBodyClass(): void {
    const body: HTMLElement = <HTMLElement>document.getElementsByTagName('BODY')[0];
    if (!body) return;
    body.classList.toggle(SHARE_MODE_BODY_CLASS_NAME, this.isShareMode);
    body.classList.toggle(SHARE_MODE_DEBUG_BODY_CLASS_NAME, this._isForcedShareMode);
  }
}
